import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [savedLocations, setSavedLocations] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [taxInfo, setTaxInfo] = useState(null);
  const [languageSettings, setLanguageSettings] = useState({});
  const [privacySettings, setPrivacySettings] = useState({});
  const [loading, setLoading] = useState(true);

  const effectiveRole = user?.active_role || user?.role;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [settingsRes, locRes] = await Promise.all([
          axios.get(`${API}/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/user/locations`).catch(() => ({ data: [] })),
        ]);
        const s = settingsRes.data || {};
        setLanguageSettings({ language: s.language || 'en', timezone: s.timezone || 'Asia/Manila' });
        setPrivacySettings({ show_location: s.show_location ?? true, allow_reviews: s.allow_reviews ?? true });
        setSavedLocations(locRes.data || []);

        // Tax info
        try { const t = await axios.get(`${API}/tax-information`); setTaxInfo(t.data); } catch (e) { console.warn('No tax information available', e?.response?.status); }
        // Credentials
        try { const c = await axios.get(`${API}/credentials/my`); setCredentials(c.data || []); } catch (e) { console.warn('Could not load credentials', e?.response?.status); }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [effectiveRole]);

  const langNames = { en: 'English', fil: 'Filipino', ceb: 'Cebuano' };
  const credLabels = {
    barangay_clearance: 'Barangay Clearance', police_clearance: 'Police Clearance',
    nbi_clearance: 'NBI Clearance', certification: 'Certification', other: 'Other Document',
  };

  const InfoRow = ({ label, value, testId }) => (
    <div className="flex justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 text-sm">{label}</span>
      <span className="text-gray-800 text-sm font-medium text-right" data-testid={testId}>{value || '—'}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" data-testid="page-title">{t('profile.title')}</h1>
          <Link
            to="/account-settings"
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg hover:shadow-lg font-semibold text-sm transition flex items-center gap-2"
            data-testid="edit-settings-link"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('profile.editInSettings')}
          </Link>        </div>

        {/* Profile Header Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="profile-header-card">
          <div className="flex items-center gap-5">
            {user.avatar ? (
              <img
                src={user.avatar.startsWith('/api') ? `${BACKEND_URL}${user.avatar}` : user.avatar}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                data-testid="profile-avatar"
              />
            ) : (
              <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white text-3xl font-bold border-2 border-gray-200">
                {user.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold" data-testid="user-name">{user.name}</h2>
                {user.id_verified && (
                  <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    {t('profile.verified')}
                  </span>
                )}
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  effectiveRole === 'admin' ? 'bg-purple-100 text-purple-700' :
                  effectiveRole === 'provider' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`} data-testid="user-role">
                  {effectiveRole === 'admin' ? t('profile.admin') : effectiveRole === 'provider' ? t('profile.provider') : t('profile.client')}
                </span>
              </div>
              {user.username && <p className="text-gray-500 text-sm">@{user.username}</p>}
              {effectiveRole === 'provider' && user.rating > 0 && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-yellow-500">★</span>
                  <span className="font-bold">{user.rating}</span>
                  <span className="text-gray-500 text-sm">({user.total_reviews} {t('profile.reviews')})</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="section-personal">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              {t('profile.personalInfo')}
            </h3>
            <InfoRow label={t('profile.fullName')} value={user.name} testId="info-name" />
            <InfoRow label={t('profile.email')} value={user.email} testId="info-email" />
            <InfoRow label={t('profile.phone')} value={user.phone} testId="info-phone" />
            <InfoRow label={t('profile.location')} value={user.location} testId="info-location" />
            {user.bio && <InfoRow label={t('profile.bio')} value={user.bio} testId="info-bio" />}
            {user.skills?.length > 0 && (
              <div className="pt-3">
                <span className="text-gray-500 text-sm">{t('profile.skills')}</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {user.skills.map((s, i) => (
                    <span key={i} className="bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full text-xs font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Saved Locations */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="section-locations">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              {t('profile.savedLocations')}
            </h3>
            {savedLocations.length === 0 ? (
              <p className="text-gray-400 text-sm">{t('profile.noLocations')}</p>
            ) : (
              savedLocations.map((loc, i) => (
                <div key={loc.id || i} className="py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-800">{loc.label || t('profile.location')}</p>
                    {loc.is_default && (
                      <span className="bg-orange-100 text-orange-600 text-xs px-1.5 py-0.5 rounded font-medium">{t('profile.default')}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                    {loc.street && <p>{t('profile.street')}: {loc.street}</p>}
                    {loc.barangay && <p>{t('profile.barangay')}: {loc.barangay}</p>}
                    {loc.city && <p>{t('profile.city')}: {loc.city}</p>}
                    {loc.province && <p>{t('profile.province')}: {loc.province}</p>}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Privacy */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="section-privacy">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              {t('profile.privacy')}
            </h3>
            <InfoRow label={t('profile.showLocation')} value={privacySettings.show_location ? t('profile.yes') : t('profile.no')} />
            <InfoRow label={t('profile.allowReviews')} value={privacySettings.allow_reviews ? t('profile.yes') : t('profile.no')} />
          </div>

          {/* Language & Currency */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="section-language">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {t('profile.languageCurrency')}
            </h3>
            <InfoRow label={t('profile.language')} value={langNames[languageSettings.language] || languageSettings.language} />
            <InfoRow label={t('profile.timezone')} value={languageSettings.timezone} />
            <InfoRow label={t('profile.currency')} value={user.currency || 'PHP'} />
          </div>

          {/* Tax Information */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="section-taxes">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              {t('profile.taxInfo')}
            </h3>
            {taxInfo ? (
              <>
                <InfoRow label={t('profile.taxIdType')} value={taxInfo.tax_id_type} />
                <InfoRow label={t('profile.taxIdNumber')} value={taxInfo.tax_id_number ? `****${taxInfo.tax_id_number.slice(-4)}` : '—'} />
                <InfoRow label={t('profile.businessName')} value={taxInfo.business_name} />
                <InfoRow label={t('profile.classification')} value={taxInfo.tax_classification} />
              </>
            ) : (
              <p className="text-gray-400 text-sm">{t('profile.noTax')}</p>
            )}
          </div>

          {/* Credentials */}
          {credentials.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 md:col-span-2" data-testid="section-credentials">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                {t('profile.documents')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {credentials.map((cred) => (
                  <div key={cred.id} className="text-center">
                    <img
                      src={cred.image_url.startsWith('http') ? cred.image_url : `${BACKEND_URL}${cred.image_url}`}
                      alt={credLabels[cred.type] || cred.type}
                      className="w-full h-20 object-cover rounded-lg border"
                    />
                    <p className="text-xs text-gray-500 mt-1">{credLabels[cred.type] || cred.type}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Member Since */}
        <div className="text-center mt-8 text-sm text-gray-400">
          {t('profile.memberSince')} {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
